import { forwardRef, type SelectHTMLAttributes } from 'react';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

interface SelectBaseProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  readonly error?: string;
  readonly helperText?: string;
  readonly options: readonly SelectOption[];
  /** Optional placeholder option as the first entry. Renders disabled + selected when value is empty. */
  readonly placeholder?: string;
}

/**
 * A visible `label` is only useful if it can be bound to a control, and the
 * binding below is `id ?? name`. Given neither, React drops both undefined
 * attributes: the <label> renders with no `for`, the control with no `id`, and
 * the two are never associated. A screen reader then announces an unlabelled
 * field and `getByLabelText` cannot find it — while the rendered output looks
 * completely normal. That invisibility is how 26 of these accumulated in a
 * single file (#29 / #1075).
 *
 * Requiring the binding in the type makes the broken state unwriteable rather
 * than merely noticeable — the same move `dog-fields.ts` made for Dog columns.
 * Controls with no visible label are unaffected: they pass no `label` at all.
 */
type LabelBinding =
  | { readonly label: string; readonly id: string }
  | { readonly label: string; readonly name: string }
  | { readonly label?: never };

export type SelectProps = SelectBaseProps & LabelBinding;

/**
 * Native select with label + error state. Mirrors HTHD admin's Select prop
 * shape (`label`, `error`, `options: [{ value, label }]`).
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { className = '', label, error, helperText, options, placeholder, id, ...props },
    ref,
  ) => {
    const selectId = id ?? props.name;
    const selectClass = [
      'ferro-field__input',
      'ferro-select',
      error ? 'ferro-select--invalid' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className="ferro-field">
        {label ? (
          <label htmlFor={selectId} className="ferro-field__label">
            {label}
          </label>
        ) : null}
        <select
          ref={ref}
          id={selectId}
          className={selectClass}
          aria-invalid={error ? true : undefined}
          {...props}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        {error ? <p className="ferro-form__error">{error}</p> :
         helperText ? <p className="ferro-field__helper">{helperText}</p> : null}
      </div>
    );
  },
);

Select.displayName = 'Select';
